/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { Stack } from '@fluentui/react/lib/Stack';
import { CustomizerContext } from '@fluentui/react/lib/Utilities';
import { useConst } from '@uifabric/react-hooks';
import * as React from 'react';
import ApprovalsListProvider from '../../../context/ApprovalsListContext/ApprovalsListProvider';
import ESignApprovalsListProvider from '../../../context/ESignApprovalsListContext/ESignApprovalsListProvider';
import { useListDataQuery } from '../../../data/client/queries/ListData.generated';
import { useSidebarDataQuery } from '../../../data/client/queries/SidebarData.generated';
import { ApprovalRequestType } from '../../../data/types.generated';
import { LogCategory, PanelRegion, ScenarioName } from '../../../logger';
import { ApprovalCreator } from '../../../service/gen';
import { getAppHost } from '../../../utilities/TeamsUtils';
import {
    ApprovalsAppCommandBar,
    ApprovalsAppFilter,
    ApprovalsAppHeader,
    ApprovalsAppList,
    ApprovalsAppSidebar,
} from '../../components';
import { EsignProviderPageContainer } from '../../Esign/EsignProviderPage/EsignProviderPage.container';
import { ApprovalsModal } from '../ApprovalsModal';
import { DataExportModal } from '../DataExport/DataExportModal';
import { TemplateManagementModal } from '../TemplateManagementModal';
import {
    getAppSidebarItemStyles,
    getInnerStackStyles,
    getScrollablePaneStackItemStyles,
    getScrollablePaneStyles,
    getStackItemStyles,
    getStackStyles,
} from './ApprovalsApp.styles';

export const ApprovalsApp: React.FC = (): JSX.Element => {
    const theme = React.useContext(CustomizerContext).customizations.settings.theme;
    const logger = getAppHost().getLogger();
    const logCategory = LogCategory.PersonalApp;
    const {
        data: { listData },
    } = useListDataQuery();

    const { data } = useSidebarDataQuery();

    // Append css class to show sidebar state in personal app
    let className = data.sidebarData?.isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed';
    if (data.sidebarData?.isFloating) {
        className += ' sidebar-floating';
    }

    const ecsConfig = getAppHost().getEcsConfig().MicrosoftTeamApprovals;
    const isDataExportEnabled = ecsConfig.DataExportEnabled;

    // Component Lifecycle
    React.useEffect((): any => {
        // Component mounted
        logger.logTraceInfo(logCategory, 'Rendered the personal app');

        return (): void => {
            // Component will unmount
            logger.logTraceInfo(logCategory, 'Closing the personal app');
        };
    }, []);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    getAppHost().getLogger().useCreateScenarioEffect(
        ScenarioName.PersonalApp,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        getAppHost().getPersonalAppEntryPoint(),
        'appBar',
        PanelRegion.Main,
        'approvals'
    );

    const ListProvider =
        listData.approvalType === ApprovalRequestType.Syntex ||
        listData.approvalType === ApprovalRequestType.ESign ||
        listData.approvalType === ApprovalRequestType.Docusign
            ? ESignApprovalsListProvider
            : ApprovalsListProvider;

    const mainContent = (
        <EsignProviderPageContainer
            showBackButton={false}
            showTrialPageButton={true}
            approvalType={listData.approvalType}
            approvalCreator={ApprovalCreator.PersonalApp}>
            <ListProvider listData={listData}>
                {useConst(
                    <ScrollablePane styles={getScrollablePaneStyles}>
                        <ApprovalsAppList />
                    </ScrollablePane>
                )}
            </ListProvider>
        </EsignProviderPageContainer>
    );

    const approvalsApp = React.useMemo((): JSX.Element => {
        return (
            <>
                <Stack styles={getStackStyles(theme)}>
                    <Stack.Item>
                        <ApprovalsAppHeader />
                    </Stack.Item>
                    <Stack.Item>
                        <ApprovalsAppCommandBar />
                    </Stack.Item>
                    <Stack.Item grow styles={getStackItemStyles}>
                        <Stack horizontal styles={getInnerStackStyles}>
                            <Stack.Item styles={getAppSidebarItemStyles}>
                                <ApprovalsAppSidebar />
                            </Stack.Item>
                            <Stack.Item styles={getScrollablePaneStackItemStyles}>{mainContent}</Stack.Item>
                        </Stack>
                    </Stack.Item>
                </Stack>
                <ApprovalsAppFilter />
                <ApprovalsModal />
                <TemplateManagementModal />
                {isDataExportEnabled && <DataExportModal />}
            </>
        );
    }, [theme, mainContent, isDataExportEnabled]);

    return <div className={className}>{approvalsApp}</div>;
};
